// Spanish copy for /face-data-policy (hand-written from copy.en.ts; same shape, same facts). The detailed documents
// it links to (the Studio biometric data retention policy and the full Photohawk Family policy) are in English.
import type { FaceDataPolicyCopy } from "./copy.en";

const CONTACT =
  "CLOUDCONCUR LTD (empresa n.º 13399913), que opera como Photohawk, 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Reino Unido. Correo: support@photohawk.com.";

const es: FaceDataPolicyCopy = {
  seo: {
    title: "Política de datos faciales | Photohawk",
    description:
      "Qué datos faciales crea Photohawk, dónde se tratan, para qué se usan y cuándo se eliminan: una política para Photohawk Studio (galerías de eventos y empresas) y otra para la app Photohawk (la app para las fotos de tu gente).",
  },
  title: "Política de datos faciales",
  version: "1.3",
  effectiveDate: "31 de octubre de 2026",
  meta: "Versión {version} · En vigor desde el {date}",
  intro: [
    "Photohawk tiene dos productos que encuentran a las personas en las fotos, y cada uno tiene su propia política de datos faciales a continuación. Photohawk Studio gestiona galerías de eventos y empresas, donde cada persona encuentra sus fotos con un selfie. La app Photohawk es para las fotos de tu gente, en tu propio teléfono.",
    "Los dos se mantienen separados. Los datos faciales de las galerías de Photohawk Studio y los de la app Photohawk nunca se comparten, se mezclan ni se comparan entre sí. Ninguno se vende ni se usa para publicidad, y los datos faciales nunca son una foto: eliminarlos nunca elimina una foto.",
  ],
  tabsLabel: "Elige un producto",
  studio: {
    id: "studio",
    tab: "Photohawk Studio",
    heading: "Photohawk Studio — galerías de eventos y empresas",
    summary:
      "La búsqueda por selfie es opcional y la activa el administrador de cada galería. Permite que una persona encuentre sus propias fotos en esa galería con un selfie.",
    sections: [
      {
        heading: "1. Qué datos faciales se crean",
        blocks: [
          {
            type: "ul",
            items: [
              "Plantillas faciales: representaciones numéricas de los rostros detectados en las fotos de una galería, guardadas en el índice de búsqueda facial de esa galería (Amazon Rekognition) o, en las galerías que usan uno de nuestros propios modelos faciales, como vectores faciales.",
              "El archivo de rostros: pequeños recortes de rostros guardados de forma privada para poder reconstruir la búsqueda facial de una galería sin volver a leer las fotos originales. Nunca se muestra a nadie ni se publica.",
              "Los selfies usados para buscar no son datos faciales que guardemos: un selfie se compara con las plantillas faciales de esa galería y después se descarta. No se guarda y no se crea ninguna plantilla facial a partir de él.",
            ],
          },
          {
            type: "p",
            text: "Encontrar personas en las fotos (cuerpos completos, sin identificar a nadie) y leer dorsales y texto no usa datos faciales. La búsqueda por dorsal no se ve afectada por esta política.",
          },
        ],
      },
      {
        heading: "2. Dónde se tratan",
        blocks: [
          {
            type: "ul",
            items: [
              "En la nube de Photohawk, en Amazon Web Services en la Unión Europea (Irlanda, eu-west-1), después de subir una foto.",
              "Cada galería tiene su propio índice de búsqueda facial (una colección de Amazon Rekognition). Un selfie solo se busca en la galería para la que se hizo.",
              "Para ahorrar tiempo, las apps de subida de Photohawk Studio pueden encontrar los rostros en el ordenador o el teléfono que sube las fotos y enviar pequeños recortes de rostros con la foto. Las plantillas faciales siempre se crean en nuestra nube.",
            ],
          },
        ],
      },
      {
        heading: "3. Para qué se usan y para qué nunca se usan",
        blocks: [
          {
            type: "p",
            text: "Los datos faciales se usan solo para que las personas encuentren sus propias fotos en esa galería con un selfie.",
          },
          {
            type: "p",
            text: "Nunca se venden, nunca se usan para publicidad, nunca se usan para identificar a nadie fuera de la galería y nunca se mezclan ni se comparan con los datos faciales de la app Photohawk.",
          },
        ],
      },
      {
        heading: "4. Cuánto tiempo se guardan y cuándo se eliminan",
        blocks: [
          {
            type: "p",
            text: "A partir del 1 de noviembre de 2026, los datos faciales se eliminan de forma permanente en el primero de estos momentos:",
          },
          {
            type: "ul",
            items: [
              "el plazo que eligió el administrador de la galería: 30, 90 o 180 días o 12 meses después de subir la foto (foto a foto, aunque la galería siga en línea). Lo predeterminado es «Sin caducidad»: nada se elimina por antigüedad;",
              "cuando se elimina la galería o la foto;",
              "30 días después de desactivar la búsqueda facial de la galería (se cancela si el administrador vuelve a activarla en esos 30 días);",
              "en un plazo de 24 horas desde que se cierra el espacio de trabajo del administrador;",
              "30 días después de que una persona nos pida eliminar sus datos faciales o retire su consentimiento;",
              "45 días después de que nuestra revisión anual determine que ya no son necesarios.",
            ],
          },
          {
            type: "p",
            text: "Archivar una galería no elimina sus datos faciales: una galería archivada sigue en línea y conserva su búsqueda facial.",
          },
          {
            type: "p",
            text: "Hasta el 31 de octubre de 2026, los datos faciales se eliminan cuando una galería caduca (en unos 60 días) y cuando se elimina una galería. Las plantillas faciales no tienen copia de seguridad, así que una eliminación es definitiva; las copias de seguridad de nuestra base de datos solo contienen referencias a rostros (nunca plantillas) y se purgan en 35 días. Las fotos, las ventas, las descargas y la búsqueda por dorsal no se ven afectadas.",
          },
          {
            type: "link",
            text: "El calendario de conservación completo y cómo funciona la eliminación (en inglés):",
            label: "Biometric data retention policy",
            href: "/biometric-data-retention",
          },
        ],
      },
      {
        heading: "5. Base jurídica",
        blocks: [
          {
            type: "p",
            text: "Fotos. Las fotos de una galería se comparten para que los participantes puedan encontrar y comprar sus propias fotos. Se tratan porque es necesario para la finalidad de la galería, y se conservan solo durante el tiempo proporcionado a esa finalidad (véase §4).",
          },
          {
            type: "p",
            text: "Búsqueda facial. Los datos faciales son datos biométricos y reciben una protección adicional:",
          },
          {
            type: "ul",
            items: [
              "Cuándo se usan: solo para que las personas puedan encontrar sus propias fotos en esa galería. Nunca se usan para identificar a nadie con ningún otro fin.",
              "Cuánto tiempo se guardan: el tiempo que elija el administrador (§4). Se eliminan cuando termina ese plazo, cuando se desactiva la búsqueda facial o se elimina la galería, o antes si alguien lo pide.",
              "A la persona que busca se le pide su consentimiento explícito antes de usar su selfie. El selfie nunca se guarda.",
              "El administrador decide si la búsqueda facial es adecuada para su evento y si está permitida en el lugar donde se celebra. Cuando la legislación local exige el consentimiento de las personas que aparecen en las fotos, el administrador debe tenerlo, por ejemplo a través de la inscripción en el evento. Si no lo tiene, debe usar en su lugar la búsqueda por dorsal y por hora, que no usa datos faciales.",
              "Cualquier persona puede pedir en cualquier momento que se eliminen sus datos faciales, y se eliminan en un plazo de 30 días (§8).",
            ],
          },
          {
            type: "p",
            text: "Para las personas de una galería, el administrador de la galería es el responsable del tratamiento; Photohawk trata los datos faciales en su nombre, con nuestro acuerdo de tratamiento de datos.",
          },
        ],
      },
      {
        heading: "6. Illinois y otros estados de EE. UU.",
        blocks: [
          {
            type: "p",
            text: "Esta política y la política de conservación de datos biométricos son nuestro calendario de conservación público y por escrito. El administrador elige cuánto tiempo se guardan los datos faciales (§4). Los administradores cuyos eventos estén sujetos a la ley de Illinois sobre información biométrica (BIPA: cuando se cumple la finalidad o en un plazo de 3 años desde la última interacción de la persona), la CUBI de Texas, la Ley de Privacidad de Colorado o la RCW 19.375 de Washington deben elegir un plazo dentro de esos límites. Ninguna ley establece un plazo único.",
          },
        ],
      },
      {
        heading: "7. Quién los trata",
        blocks: [
          {
            type: "p",
            text: "Amazon Web Services (Amazon Rekognition y almacenamiento), región UE (Irlanda), como nuestro subencargado, solo siguiendo nuestras instrucciones. No revelamos datos faciales a nadie más salvo que la ley lo exija.",
          },
        ],
      },
      {
        heading: "8. Solicitudes y contacto",
        blocks: [
          {
            type: "ul",
            items: [
              "En la galería: abre la búsqueda por selfie y elige “Make a request”. La solicitud llega al administrador de la galería y a Photohawk.",
              "Por correo: support@photohawk.com, indicando a qué galería o evento se refiere. Atendemos la solicitud en un plazo de 30 días.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "El calendario de conservación detallado de Studio (en inglés):",
      label: "Biometric data retention policy",
      href: "/biometric-data-retention",
    },
  },
  family: {
    id: "family",
    tab: "App Photohawk",
    heading: "La app Photohawk — para las fotos de tu gente",
    summary:
      "Agrupar a las personas de tus fotos es lo que hace la app (en las tiendas de apps como “Photohawk”), así que pide tu consentimiento explícito antes de crear datos faciales: primero para agrupar caras y después para compararlas en la nube. Las caras se encuentran en tu teléfono; para agruparlas, solo pequeños recortes de caras (nunca tus fotos) van a tu propia colección privada de caras en nuestra nube. Puedes eliminarlo todo cuando quieras.",
    sections: [
      {
        heading: "1. Qué datos faciales se crean",
        blocks: [
          {
            type: "ul",
            items: [
              "En tu teléfono: dónde están las caras en cada foto o vídeo, qué caras son de la misma persona, los nombres y correcciones que añades, cumpleaños y parentescos, y el rango de edad estimado de cada cara (que devuelve la nube, que no lo guarda). Todo esto se queda solo en tu teléfono.",
              "En la nube: pequeños recortes de caras (nunca fotos ni vídeos completos) y las plantillas faciales (una lista de números por cara) y los grupos que nuestro servicio en la nube crea a partir de ellos.",
            ],
          },
          {
            type: "p",
            text: "Reconocer mascotas y el tipo de escena de tus fotos no usa datos faciales.",
          },
        ],
      },
      {
        heading: "2. Dónde se tratan",
        blocks: [
          {
            type: "ul",
            items: [
              "En tu teléfono: las caras se encuentran en el teléfono y los datos faciales se guardan en el almacenamiento privado de la app, excluidos de las copias de seguridad.",
              "En la nube: los recortes de caras van a tu propia colección de caras en Amazon Rekognition (Amazon Web Services, región UE (Irlanda)), que solo contiene tus caras. Los recortes se eliminan en cuanto se comparan (lo que quede se elimina al cabo de un día).",
              "Nunca recibimos tus fotos ni vídeos para esto (salvo las fotos que compartes, cifradas para que no podamos verlas, y las impresiones que pides), ni los nombres, cumpleaños o parentescos que añades.",
            ],
          },
        ],
      },
      {
        heading: "3. Para qué se usan y para qué nunca",
        blocks: [
          {
            type: "p",
            text: "Los datos faciales solo se usan para agrupar tus fotos y vídeos por las personas que aparecen, para buscar por persona, para ayudarte a elegir qué fotos compartir y para que quien parezca un niño nunca se ofrezca como destinatario de fotos.",
          },
          {
            type: "p",
            text: "Nunca se venden, nunca se usan para publicidad, perfiles ni comprobaciones de identidad, nunca para identificar a nadie fuera de tus propias fotos, nunca se comparan entre usuarios, nunca se usan para entrenar modelos y nunca se mezclan ni se comparan con los datos faciales de Photohawk Studio. Nunca forman parte de lo que compartes.",
          },
        ],
      },
      {
        heading: "4. Cuánto tiempo se guardan y cuándo se eliminan",
        blocks: [
          {
            type: "p",
            text: "En tu teléfono, los datos faciales se guardan hasta que tocas “Eliminar todos los datos faciales”, eliminas tu cuenta, eliminas la app o pasas 3 años sin usar Photohawk. Los datos faciales de una foto se van cuando la foto sale de tu teléfono.",
          },
          {
            type: "p",
            text: "En la nube, los datos faciales se eliminan cuando tocas “Eliminar todos los datos faciales”, cuando eliminas tu cuenta de Photohawk, cuando nos lo pides (en un plazo de 30 días) y tras 3 años sin usar Photohawk. No se eliminan cuando termina un plan o una prueba gratuita.",
          },
          {
            type: "p",
            text: "La eliminación en la nube borra toda tu colección de caras. Eliminar datos faciales nunca elimina fotos ni vídeos.",
          },
        ],
      },
      {
        heading: "5. Base jurídica y consentimiento",
        blocks: [
          {
            type: "p",
            text: "Nos basamos en tu consentimiento explícito (art. 9.2.a del RGPD y su equivalente en otras leyes), pedido dos veces y por separado antes de que la app analice tus fotos: para agrupar caras (la casilla empieza sin marcar) y para compararlas en la nube. Ambos son necesarios para usar la app; si no aceptas, no se crean datos faciales y puedes eliminar tu cuenta. Puedes retirarlo en cualquier momento tocando “Eliminar todos los datos faciales”: se elimina todo y la app vuelve a preguntar antes de crear más. Si el texto cambia, la app vuelve a preguntar.",
          },
        ],
      },
      {
        heading: "6. Illinois",
        blocks: [
          {
            type: "p",
            text: "La app Photohawk no se ofrece a quienes le indican que viven en Illinois (la app lo pregunta; no usa el GPS ni tu dirección IP). Nuestro servicio también rechaza la comparación en la nube para Illinois, y si cambias tu lugar de residencia a Illinois, los datos faciales existentes se eliminan en el teléfono y en la nube.",
          },
        ],
      },
      {
        heading: "7. Quién los trata",
        blocks: [
          {
            type: "p",
            text: "Amazon Web Services (Amazon Rekognition), región UE (Irlanda), como encargado del tratamiento, solo según nuestras instrucciones. Los datos faciales que se quedan en tu teléfono nunca se envían a nadie.",
          },
        ],
      },
      {
        heading: "8. Solicitudes y contacto",
        blocks: [
          {
            type: "ul",
            items: [
              "En la app: Compartir y privacidad → “Eliminar todos los datos faciales”, o Eliminar cuenta.",
              "Por correo: support@photohawk.com, para acceder a tus datos faciales en la nube o eliminarlos.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "La política de datos faciales completa de la app Photohawk, con su calendario de conservación (en inglés):",
      label: "Photohawk app: Face Data Policy",
      href: "/face-data-policy/family",
    },
  },
  changelog: {
    title: "Cambios",
    items: [
      "1.3, 31 de octubre de 2026: Photohawk Studio, sección 4: el administrador de la galería elige cuánto tiempo se guardan los datos faciales («Sin caducidad» por defecto, o 30, 90 o 180 días o 12 meses después de subir la foto); archivar una galería ya no elimina sus datos faciales; los datos faciales se eliminan 30 días después de desactivar la búsqueda facial, al eliminar la galería o la foto, y en un plazo de 24 horas desde el cierre de un espacio de trabajo. Las secciones 5 y 6 se adaptan.",
      "1.2, 28 de octubre de 2026: la app Photohawk: agrupar caras y compararlas en la nube es necesario para usar la app (dos consentimientos separados, que se retiran con “Eliminar todos los datos faciales”); los datos faciales en la nube ya no se eliminan 30 días después de que termine un plan; la app no se ofrece en Illinois; las edades estimadas, los cumpleaños y los parentescos se quedan en el teléfono. Política completa de la app v1.1.",
      "1.1, 22 de octubre de 2026: Photohawk Studio, sección 5 reescrita: cuál es la base jurídica para las fotos y para la búsqueda facial, y qué decide el administrador.",
      "1.0, 20 de octubre de 2026: primera versión. Una página con una política propia para Photohawk Studio y otra para Photohawk Family; la política de datos faciales de Photohawk Family deja de ser un borrador.",
    ],
  },
  seeAlso: {
    text: "Cómo se tratan los datos faciales en Photohawk Studio y en la app Photohawk:",
    label: "Política de datos faciales",
  },
  familyPage: {
    seoTitle: "Photohawk app: Face Data Policy | Photohawk",
    description:
      "La política de datos faciales completa de la app Photohawk: qué datos faciales crea, dónde se guardan y su calendario de conservación y destrucción.",
    back: "Política de datos faciales de los dos productos",
    englishOnly: "Este documento solo está disponible en inglés.",
  },
};

export default es;
